import { useState, createContext } from "react";
import { useDispatch } from "react-redux";

import {
  setLoadingFalseAction,
  setLoadingTrueAction,
} from "../../../redux/actions/loadingActions/loadingActions";
import {
  toastErrorMessage,
  toastSuccessMessage,
} from "../../../utils/toastMessage/toastMessage";
import {
  getCategoriesReceptionService,
  createCategoryReceptionService,
  deleteCategoryReceptionService,
  editCategoryReceptionService,
  changeCategoryActiveReceptionService,
} from "../../../services/receptionServices";
import { validateFile } from "../../../utils";
import { uploadTypeItems } from "../../../utils/enum";
import { uploadUserService } from "../../../services/userServices";
import { setMetaAction } from "../../../redux/actions/metaActions/metaActions";

export const categoryReceptionContext = createContext({
  categories: [],
  setCategories: () => {},
  id: "",
  setId: () => {},
  filter: "",
  setFilter: () => {},
  sort: "",
  setSort: () => {},
  start: "",
  setStart: () => {},
  end: "",
  setEnd: () => {},
  pageCount: null,
  setPageCount: () => {},
  pageNumber: null,
  setPageNumber: () => {},
  itemsCount: null,
  setItemsCount: () => {},
  isShowModal: null,
  setIsShowModal: () => {},
  handleShowModal: () => {},
  handleHideModal: () => {},
  handleGetCategories: () => {},
  handleCreateCategory: () => {},
  handleEditCategory: () => {},
  handleDeleteCategory: () => {},
  handleChangeCategoryActive: () => {},
  handleUploadFile: () => {},
});

const CategoryReceptionContext = ({ children }) => {
  const dispatch = useDispatch();

  const [categories, setCategories] = useState([]);
  const [id, setId] = useState("");
  const [filter, setFilter] = useState("");
  const [sort, setSort] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [pageCount, setPageCount] = useState(1);
  const [pageNumber, setPageNumber] = useState(1);
  const [itemsCount, setItemsCount] = useState(100);
  const [isShowModal, setIsShowModal] = useState(false);

  // Modal Operation
  const handleShowModal = (val, setValue) => {
    setIsShowModal(true);

    if (val) {
      setId(val._id);
      setValue("picture", val.picture);
      setValue("title", val.title);
      setValue("description", val.description);
    }
  };
  const handleHideModal = () => {
    setIsShowModal(false);
    setId("");
  };

  // Get Categories
  const handleGetCategories = async (event) => {
    event?.preventDefault();

    const objData = {
      ...(start && { start }),
      ...(end && { end }),
      filter,
      pageNumber,
      sort,
      itemsCount,
    };
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await getCategoriesReceptionService(objData);
      if (status === 200) {
        setCategories(data.result?.categories);
        setPageCount(data?.result?.allPagesCount);
        dispatch(setMetaAction());
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Create Category
  const handleCreateCategory = async (userData) => {
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await createCategoryReceptionService(userData);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        handleGetCategories();
        handleHideModal();
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
      dispatch(setLoadingFalseAction());
    }
  };

  // Edit Category
  const handleEditCategory = async (userData) => {
    const objData = {
      id,
      ...userData,
    };
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await editCategoryReceptionService(objData);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        handleGetCategories();
        handleHideModal();
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
      dispatch(setLoadingFalseAction());
    }
  };

  // Delete Category
  const handleDeleteCategory = async (id) => {
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await deleteCategoryReceptionService(id);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        handleGetCategories();
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
      dispatch(setLoadingFalseAction());
    }
  };

  // Change Category Active
  const handleChangeCategoryActive = async (id) => {
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await changeCategoryActiveReceptionService(id);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        handleGetCategories();
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
      dispatch(setLoadingFalseAction());
    }
  };

  // Upload File
  const handleUploadFile = async (file, callback) => {
    const fileErrorMessage = validateFile(file);
    if (fileErrorMessage) return toastErrorMessage(fileErrorMessage);

    const objData = { file };
    const params = { destination: uploadTypeItems[4] };
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await uploadUserService(objData, params);
      if (status === 200) {
        callback && callback(data.result);
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  return (
    <categoryReceptionContext.Provider
      value={{
        categories,
        setCategories,
        id,
        setId,
        filter,
        setFilter,
        sort,
        setSort,
        start,
        setStart,
        end,
        setEnd,
        pageCount,
        setPageCount,
        pageNumber,
        setPageNumber,
        itemsCount,
        setItemsCount,
        isShowModal,
        setIsShowModal,
        handleShowModal,
        handleHideModal,
        handleGetCategories,
        handleCreateCategory,
        handleEditCategory,
        handleDeleteCategory,
        handleChangeCategoryActive,
        handleUploadFile,
      }}
    >
      {children}
    </categoryReceptionContext.Provider>
  );
};

export default CategoryReceptionContext;
